import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import { PageBanner } from '../Banners/PageBanner'

import { useRequests } from '../../requests/useRequests'
import { formatDate } from '../../utils/dateFormatter'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { BlogPost } from '../../types/blog'

const PlusIcon = () => (
  <svg className='tw-w-4 tw-h-4' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path d='M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z' />
  </svg>
)

const ExclamationCircleIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-mr-2' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z' clipRule='evenodd' />
  </svg>
)

const NewspaperIcon = () => (
  <svg className='tw-w-16 tw-h-16 tw-mb-4 tw-text-slate-300' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18V7.875c0-.621.504-1.125 1.125-1.125H6.75M12 7.5V9m0 3V9m0 3v2.25m0 3v-2.25m0 0l1.5-1.5m-1.5 1.5l-1.5-1.5m3-3l1.5-1.5m-1.5 1.5l-1.5-1.5' />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z' clipRule='evenodd' />
  </svg>
)

const ArrowRightIcon = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z' clipRule='evenodd' />
  </svg>
)

const Blog = ({ addPageBanner = true }: { addPageBanner?: boolean }): JSX.Element => {
  const [blogPostsData, setBlogPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)

  const requests = useRequests()
  const { username, isSuperadmin } = useSelector((state: State) => state.user)

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setLoading(true)
        const response = await requests.getBlogPosts({ page: currentPage, limit: 6 })

        const formattedPosts = response.data.data.map((post: any) => ({
          ...post,
          date: formatDate(post.date)
        }))

        setBlogPosts(formattedPosts)
        setTotalPages(response.data.totalPages)
        setError(null)
      } catch (err) {
        setError('Nie udało się załadować artykułów. Spróbuj ponownie później.')
        console.error('Błąd podczas pobierania artykułów:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchBlogPosts()
  }, [currentPage, requests])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      window.scrollTo(0, 0)
    }
  }

  const PaginationLink: React.FC<{
    page?: number
    onClick: () => void
    isActive?: boolean
    isDisabled?: boolean
    children: React.ReactNode
  }> = ({ onClick, isActive, isDisabled, children }) => (
    <li>
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`
          tw-flex tw-items-center tw-justify-center tw-px-3 tw-h-8 tw-leading-tight 
          tw-border tw-transition-colors tw-duration-150
          ${isActive
                ? 'tw-bg-green tw-text-white tw-border-green hover:tw-bg-opacity-90'
                : 'tw-text-slate-500 tw-bg-white tw-border-slate-300 hover:tw-bg-slate-100 hover:tw-text-slate-700'
            }
          ${isDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'}
          tw-rounded-md 
        `}
      >
        {children}
      </button>
    </li>
  )

  return (
    <>
      <Header />
      {addPageBanner && (
        <PageBanner title='Blog' description='Dowiedz się więcej o naszych projektach i inspiracjach' />
      )}
      <section className='tw-pt-28 md:tw-pt-32 lg:tw-pt-36 tw-pb-12 md:tw-pb-16 lg:tw-pb-20'>
        <div className='tw-container tw-mx-auto tw-px-4'>
          {username && isSuperadmin && (
            <div className='tw-flex tw-justify-end tw-mb-8 md:tw-mb-12'>
              <Link
                to='/blog/add'
                className='tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-green hover:tw-bg-opacity-90 tw-text-white tw-text-sm tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors'
              >
                <PlusIcon />
                <span className='tw-ml-2'>Dodaj nowy artykuł</span>
              </Link>
            </div>
          )}

          {loading
            ? (
              <div className='tw-text-center tw-py-10'>
                <svg className='tw-animate-spin tw-mx-auto tw-h-12 tw-w-12 tw-text-green' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                  <circle className='tw-opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                  <path className='tw-opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                </svg>
                <p className='tw-mt-3 tw-text-slate-500'>Wczytywanie artykułów...</p>
              </div>
              )
            : error
              ? (
                <div className='tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded-md tw-relative tw-flex tw-items-center' role='alert'>
                  <ExclamationCircleIcon />
                  <span>{error}</span>
                </div>
                )
              : (
                <>
                  {blogPostsData.length > 0
                    ? (
                      <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 lg:tw-gap-8'>
                        {blogPostsData.map(post => (
                            <div key={post.id} className='tw-bg-white tw-rounded-lg tw-shadow-lg tw-overflow-hidden tw-flex tw-flex-col tw-h-full group'>
                              <Link to={`/blog/${post.slug}`} className='tw-block tw-aspect-[16/10] tw-overflow-hidden'>
                                <img
                  src={post.imagePresignedUrl || post.imageUrl || `/api/uploads/image/${post.image}`}
                  alt={post.title}
                  className='tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform tw-duration-300 tw-ease-in-out'
                />
                              </Link>
                              <div className='tw-p-5 md:tw-p-6 tw-flex tw-flex-col tw-flex-grow'>
                                <div className='tw-mb-2 tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-1'>
                  {post.categories.slice(0, 2).map((category, index) => (
                                <Link
                                    to={`/blog?categories=${category.toLowerCase()}`}
                                    key={index}
                                    className='tw-text-xs tw-font-medium tw-text-indigo-600 hover:tw-text-indigo-800 tw-px-2 tw-py-0.5 tw-bg-indigo-100 tw-rounded-full'
                                  >
                                    {category}
                                  </Link>
                              ))}
                </div>
                                <p className='tw-text-xs tw-text-slate-500 tw-mb-2'>{post.date}</p>
                                <h3 className='tw-text-lg md:tw-text-xl tw-font-semibold tw-text-teal hover:tw-text-green tw-mb-3 tw-leading-tight'>
                  <Link to={`/blog/${post.slug}`} className='tw-line-clamp-2'>{post.title}</Link>
                </h3>
                                <p className='tw-text-sm tw-text-slate-600 tw-mb-4 tw-flex-grow tw-line-clamp-3'>
                  {post.description}
                </p>
                                <Link
                  to={`/blog/${post.slug}`}
                  className='tw-mt-auto tw-inline-flex tw-items-center tw-text-green hover:tw-underline tw-font-semibold tw-text-sm group'
                >
                                Czytaj więcej
                  <ArrowRightIcon />
                </Link>
                              </div>
                            </div>
                          ))}
                      </div>
                      )
                    : (
                      <div className='tw-col-span-full tw-text-center tw-py-10 md:tw-py-16'>
                        <NewspaperIcon />
                        <p className='tw-text-xl tw-text-slate-700 tw-mt-2'>Brak artykułów do wyświetlenia.</p>
                        <p className='tw-text-slate-500 tw-mt-1'>Sprawdź ponownie później lub przejdź do innej sekcji.</p>
                      </div>
                      )}

                  {totalPages > 1 && blogPostsData.length > 0 && (
                    <div className='tw-flex tw-justify-center tw-mt-12 md:tw-mt-16'>
                      <nav aria-label='Paginacja'>
                        <ul className='tw-flex tw-items-center -tw-space-x-px tw-h-10 tw-text-base'>
                            <PaginationLink
                              onClick={() => handlePageChange(currentPage - 1)}
                              isDisabled={currentPage === 1}
                            >
                              <span className='tw-sr-only'>Poprzednia</span>
                              <ArrowLeftIcon />
                            </PaginationLink>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <PaginationLink
                                key={page}
                                onClick={() => handlePageChange(page)}
                                isActive={currentPage === page}
                              >
                                {page}
                              </PaginationLink>
                            ))}

                            <PaginationLink
                              onClick={() => handlePageChange(currentPage + 1)}
                              isDisabled={currentPage === totalPages}
                            >
                              <span className='tw-sr-only'>Następna</span>
                              <ArrowRightIcon />
                            </PaginationLink>
                          </ul>
                      </nav>
                    </div>
                  )}
                </>
                )}
        </div>
      </section>
    </>
  )
}

export default Blog
